import { PayloadAction, createSlice } from '@reduxjs/toolkit'
import { StoreData } from '../reducers/typings'
import {
  DEFAULT_COMPANY_HEADER_DISPLAY_SETTINGS,
  ICompanyHeaderDisplaySettingsDTO,
  State,
} from './typings'
import {
  getCompanyHeaderDisplaySettings,
  updateCompanyHeaderDisplaySettings,
} from './api'

export const initialState: State = {
  settings: DEFAULT_COMPANY_HEADER_DISPLAY_SETTINGS,
  loading: false,
  saving: false,
}

/**
 * Company header display settings. `setCompanyHeaderDisplaySettings` applies a
 * change optimistically before the PUT resolves. A rejected update does not
 * touch `settings`: the consuming hook holds the previous value and must
 * dispatch it back through `setCompanyHeaderDisplaySettings` to roll back.
 */
export const companyHeaderDisplaySettings = createSlice({
  name: 'companyHeaderDisplaySettings',
  initialState,
  reducers: {
    setCompanyHeaderDisplaySettings: (
      state,
      action: PayloadAction<ICompanyHeaderDisplaySettingsDTO>,
    ) => {
      state.settings = action.payload
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getCompanyHeaderDisplaySettings.pending, (state) => {
        state.loading = true
      })
      .addCase(
        getCompanyHeaderDisplaySettings.fulfilled,
        (state, { payload }) => {
          state.settings = payload
          state.loading = false
        },
      )
      .addCase(getCompanyHeaderDisplaySettings.rejected, (state) => {
        state.loading = false
      })
      .addCase(updateCompanyHeaderDisplaySettings.pending, (state) => {
        state.saving = true
      })
      .addCase(
        updateCompanyHeaderDisplaySettings.fulfilled,
        (state, { payload }) => {
          state.settings = payload
          state.saving = false
        },
      )
      .addCase(updateCompanyHeaderDisplaySettings.rejected, (state) => {
        state.saving = false
      })
  },
})

export const { setCompanyHeaderDisplaySettings } =
  companyHeaderDisplaySettings.actions

export default companyHeaderDisplaySettings.reducer

export const selectCompanyHeaderDisplaySettings = (state: StoreData) =>
  state.companyHeaderDisplaySettings.settings

export const selectCompanyHeaderDisplaySettingsLoading = (state: StoreData) =>
  state.companyHeaderDisplaySettings.loading

export const selectCompanyHeaderDisplaySettingsSaving = (state: StoreData) =>
  state.companyHeaderDisplaySettings.saving
